import { Space, Typography } from 'antd';
import type { ReactNode } from 'react';

interface PageHeaderProps {
  /** 主标题（H3） */
  title: ReactNode;
  /** 副标题/统计信息（如 "1,234 条 · 今日新增 12"）*/
  subtitle?: ReactNode;
  /** 标题右侧 inline tag (eg 已选 N 项) */
  badge?: ReactNode;
  /** 右侧操作区 (按钮/搜索框等) */
  extra?: ReactNode;
  /** 标题下方的页签或二级导航。 */
  subnav?: ReactNode;
  /** 标题图标（来自 lucide-react，例 <Building2 size={22} />）*/
  icon?: ReactNode;
  /** 是否显示底部分隔线（默认 true）*/
  divider?: boolean;
  /** 紧凑列表头：标题与统计信息置顶，工具栏在下一行保持单行。 */
  compact?: boolean;
}

/**
 * 列表页 / 详情页通用 PageHeader (M37, Tier 3)
 *
 * 灵感: Linear / Vercel / Aion 的 page-header
 *   - 标题 + 副标题（统计行）+ 操作区一行
 *   - 默认底部 1px 分隔线
 *
 * 用法:
 *   <PageHeader
 *     title="客户"
 *     subtitle="共 1,234 个 · 今日新增 12"
 *     extra={<Space><Search /><Button>新建</Button></Space>}
 *     icon={<Building2 size={22} />}
 *   />
 */
export function PageHeader({
  title,
  subtitle,
  badge,
  extra,
  subnav,
  icon,
  divider = true,
  compact = false,
}: PageHeaderProps) {
  return (
    <div
      className="crest-page-header"
      style={{
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        flexWrap: compact ? 'nowrap' : 'wrap',
        alignItems: compact ? 'stretch' : 'flex-start',
        justifyContent: 'space-between',
        gap: compact ? 10 : 16,
        padding: compact ? '4px 0 12px' : '4px 0 16px',
        marginBottom: 16,
        borderBottom: divider ? '1px solid #f0f1f3' : 'none',
      }}
    >
      <div
        style={{
          minWidth: compact ? 0 : 220,
          flex: compact ? '0 0 auto' : '1 1 260px',
        }}
      >
        <Space size={10} align="center" style={{ marginBottom: 4 }}>
          {icon && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(0, 120, 212, 0.08)',
                color: '#0078d4',
              }}
            >
              {icon}
            </span>
          )}
          <Typography.Title
            level={3}
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              lineHeight: 1.3,
              wordBreak: 'keep-all',
              overflowWrap: 'normal',
            }}
          >
            {title}
          </Typography.Title>
          {badge}
        </Space>
        {subtitle && (
          <Typography.Text
            type="secondary"
            style={{
              fontSize: 13,
              display: 'block',
              paddingLeft: !compact && icon ? 42 : 0,
              whiteSpace: compact ? 'nowrap' : undefined,
            }}
          >
            {subtitle}
          </Typography.Text>
        )}
      </div>
      {subnav && <div style={{ width: '100%', minWidth: 0 }}>{subnav}</div>}
      {extra && (
        <div
          className="crest-page-header-extra"
          style={compact ? { width: '100%', minWidth: 0 } : undefined}
        >
          {extra}
        </div>
      )}
    </div>
  );
}
